<template>
  <div style="min-height: 450px; height: 450px;">
    <div v-if="loading">
      <loading-container />
    </div>
    <app-echart-scatter
      v-else
      ref="chart"
      :key="chartKey"
      :option-data="chartData"
      style="height: 100%; width: 100%;"
    />
  </div>
</template>

<script>
import AppEchartScatter from '@core/components/charts/echart/AppEchartScatter.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import renderChartToBase64 from './offscreenRender'

export default {
  components: {
    AppEchartScatter,
    LoadingContainer,
  },
  props: {
    figureTwo: {
      type: Object,
      default: () => ({
        datapoints: {},
        chartsettings: {},
      }),
    },
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  data() {
    return {
      loading: true,
      chartKey: 0,
      chartData: {
        legend: {},
        grid: {},
        xAxis: {},
        yAxis: {},
        series: [],
      },
      cachedImage: null,
    }
  },
  computed: {
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
  },
  watch: {
    figureTwo: {
      handler(newData) {
        if (newData && newData.datapoints && Object.keys(newData.datapoints).length > 0) {
          this.processPayoffData(newData)
        }
      },
      immediate: true,
      deep: true,
    },
    skin() {
      // Re-process data when theme changes
      if (this.figureTwo && this.figureTwo.datapoints && Object.keys(this.figureTwo.datapoints).length > 0) {
        this.processPayoffData(this.figureTwo)
        this.chartKey += 1
      }
    },
  },
  mounted() {
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
  },
  methods: {
    processPayoffData(figureData) {
      const datapointsArray = Object.values(figureData.datapoints)

      if (datapointsArray.length === 0) {
        this.loading = false
        return
      }

      // Convert data to ECharts format [x, y]
      const payoffData = datapointsArray.map(point => [
        parseFloat(point[0]),
        parseFloat(point[1]),
      ])

      this.chartData = {
        legend: {
          show: false,
        },
        grid: {
          left: 60,
          right: 20,
          bottom: 60,
          top: 20,
          containLabel: true,
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            snap: false,
            crossStyle: {
              color: '#999',
            },
          },
          formatter: params => {
            if (params && params.length > 0) {
              const pointData = params[0].data
              const underlying = Number(pointData[0]).toFixed(2)
              const payoff = Number(pointData[1]).toFixed(2)
              return `Underlying: ${underlying}%<br/>Payoff: ${payoff}%`
            }
            return ''
          },
        },
        xAxis: {
          type: 'value',
          name: 'Final underlying value (%)',
          nameLocation: 'middle',
          nameGap: 35,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
          },
          scale: true,
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Product payoff (%)',
          nameLocation: 'middle',
          nameGap: 50,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
          },
          scale: true,
          splitLine: {
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: 'Payoff',
            type: 'scatter',
            data: payoffData,
            symbolSize: 6,
            itemStyle: {
              color: 'lightblue',
            },
          },
        ],
      }
      this.cachedImage = renderChartToBase64(this.chartData)
      this.loading = false
    },
    resizeChart() {
      if (this.$refs.chart) {
        this.$refs.chart.resize()
      }
    },
    getDataURL() {
      return this.cachedImage || null
    },
  },
}
</script>
